import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { getUser, signIn as sendSignInRequest } from '../api/auth';
import type { User, AuthContextType } from '../types';
import { signInWithEmailAndPassword, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';

const AuthContext = createContext<AuthContextType>({ loading: false } as AuthContextType);

function AuthProvider(props: React.PropsWithChildren<unknown>) {
  const [user, setUser] = useState<User>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Wait for Firebase to rehydrate session
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const idToken = await firebaseUser.getIdToken();
        localStorage.setItem('authToken', idToken); // optional
        const result = await getUser(); // Your API that returns user data
        if (result.isOk) {
          setUser(result.data);
        }
      } else {
        setUser(undefined);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    let firebaseUser;
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      firebaseUser = userCredential.user;
    } catch (error) {
      console.error(error);
      return { isOk: false, error: 'Firebase sign-in failed' };
    }

    const idToken = await firebaseUser.getIdToken();
    localStorage.setItem('authToken', idToken); // optional

    const result = await sendSignInRequest(firebaseUser);
    if (result.isOk) {
      setUser(result.data);
    }

    return result;
  }, []);

  const signOut = useCallback(() => {
    setUser(undefined);
    auth.signOut().catch(console.error);
    localStorage.removeItem('authToken');
  }, []);

  return (
    <AuthContext.Provider value={{ user, signIn, signOut, loading }} {...props} />
  );
}

const useAuth = () => useContext(AuthContext);

export { AuthProvider, useAuth };
